<template>
  <div>
    <HeaderWithIcon title="Confirm Details" />
    <div v-if="showNewProfessionalBanner" class="new-professionals-banner">
      <h4>Congratulations.</h4>
      <p>
        Based on the information you've entered, you've qualified for the New
        Professionals rate! This will be applied automatically on the payment
        step.
      </p>
    </div>
    <template v-if="existing">
      <section class="review-section">
        <h3>About you</h3>
        <p>{{ membership }}</p>
        <p>{{ lastName }}</p>
        <p>{{ membershipNumber }}</p>
        <p>{{ dateOfBirth }}</p>
      </section>
    </template>
    <template v-else>
      <section class="review-section" v-if="firstName">
        <h3>About you</h3>
        <p>{{ title }} {{ firstName }} {{ middleNames }} {{ lastName }}</p>
        <p>{{ fee ? fee.title : membershipType }}</p>
        <p>{{ dateOfBirth }}</p>
        <router-link
          :to="{
            name: 'Contact Details',
            query: { fromReview: 'true' },
          }"
          class="btn btn--default"
        >
          Edit
        </router-link>
        <hr />
      </section>
      <section class="review-section" v-if="street">
        <h3>Contact details</h3>
        <p>{{ street }}</p>
        <p>{{ town }}</p>
        <p>{{ county }}</p>
        <p>{{ postCode }}</p>
        <p>{{ country }}</p>
        <router-link
          :to="{
            name: 'Contact Details',
            query: { fromReview: 'true' },
          }"
          class="btn btn--default"
        >
          Edit
        </router-link>
        <hr />
      </section>
      <section class="review-section" v-if="chosenWorkplace.name != ''">
        <h3>Work details</h3>
        <p>{{ chosenWorkplace.name }}</p>
        <p>{{ chosenWorkplace.trustName }}</p>
        <p>{{ chosenWorkplace.regionName }}</p>
        <p>{{ workEmail }}</p>
        <div
          class="form-control-item form-control-item--checkbox"
          v-if="isAgencyWorker"
          style="max-width: 80%; margin-top: 1em"
        >
          <input type="checkbox" checked disabled />
          <label style="align-items: flex-start; height: unset;">
            If you do not hold a current contract of employment with the NHS
            under Agenda for change terms and conditions, please tick this box.
          </label>
        </div>
        <router-link
          :to="{ name: 'Work Details', query: { fromReview: 'true' } }"
          class="btn btn--default"
        >
          Edit
        </router-link>
        <hr />
      </section>
      <section class="review-section" v-if="university || dateQualified">
        <h3>Education details</h3>
        <p v-if="university">{{ university }}</p>
        <p v-if="universityOtherCountry">{{ universityOtherCountry }}</p>
        <p v-if="universityOtherName">{{ universityOtherName }}</p>
        <p v-if="course && fullPartTime">{{ course }} ({{ fullPartTime }})</p>
        <p v-if="discipline">{{ discipline }}</p>
        <p v-if="yearOfStudy">{{ yearOfStudy }}</p>
        <p v-if="qualificationOnEntry">{{ qualificationOnEntry }}</p>
        <p v-if="startDate !== '0001-01-01T00:00:00'">
          {{ new Date(startDate).toLocaleDateString("en-GB") }}
          ({{ lengthOfCourse }} years)
        </p>
        <p v-if="dateQualified && dateQualified !== '0001-01-01T00:00:00'">
          {{ new Date(dateQualified).toLocaleDateString("en-GB") }}
        </p>
        <p
          v-if="
            expectedQualified && expectedQualified !== '0001-01-01T00:00:00'
          "
        >
          {{ new Date(expectedQualified).toLocaleDateString("en-GB") }}
        </p>

        <router-link
          :to="{
            name: 'Place of Study',
            query: {
              fromReview: 'true',
            },
          }"
          class="btn btn--default"
        >
          Edit
        </router-link>
        <hr />
      </section>
      <section class="review-section">
        <h3>Diversity details</h3>
        <p v-if="gender">{{ gender }}</p>
        <p v-if="ethnicity">{{ ethnicity }}</p>
        <p v-if="nationality">{{ nationality }}</p>
        <p>
          Do you consider yourself to have a disibility?
          <span v-if="hasDisability">Yes</span><span v-else>No</span>
        </p>

        <router-link
          :to="{
            name: 'Diversity Details',
            query: {
              fromReview: 'true',
            },
          }"
          class="btn btn--default"
        >
          Edit
        </router-link>
        <hr />
      </section>
    </template>
    <section>
      <ValidationObserver ref="form">
        <form-component :page="page" />
      </ValidationObserver>
    </section>
    <section class="controls">
      <router-link class="ctrl ctrl--begin" :to="backTo">
        Back
      </router-link>
      <button role="button" class="btn btn--positive" @click="moveNext">
        {{ isActivating ? "Complete" : "Confirm" }}
      </button>
    </section>
    <notification />
  </div>
</template>

<script>
import FormComponent from "@/components/FormComponent";
import HeaderWithIcon from "@/components/HeaderWithIcon";
import Notification from "@/components/Notification";
import { ReviewDefinition } from "@/definitions/section-definitions";
import { registrationConfig } from "@/helpers/config";
import { doPatch } from "@/helpers/fetch-helper";
import { useMemberDetails } from "@/helpers/member-details";
import { useNotification } from "@/helpers/notification";
import { filterPages } from "@/helpers/page";
import { useShowSpinner } from "@/helpers/showSpinner";
import { useWorkplaceSearch } from "@/helpers/workplace-search";
import { computed, ref } from "@vue/composition-api";
import { ValidationObserver } from "vee-validate";

export default {
  components: {
    Notification,
    HeaderWithIcon,
    FormComponent,
    ValidationObserver,
  },
  setup(_, { root }) {
    const form = ref(null);
    const {
      paymentUrl,
      membershipNumber,
      membershipType,
      membershipStatusType,
      existing,
      gender,
      dateOfBirth,
      university,
      universityOtherCountry,
      discipline,
      qualificationOnEntry,
      fullPartTime,
      course,
      isActivating,
      country,
      nationality,
      ethnicity,
      whereFound,
      whereFoundOther,
      ...memberDetails
    } = useMemberDetails();
    const { chosenWorkplace, isAgencyWorker } = useWorkplaceSearch();
    const { showNotification } = useNotification();
    const { setShowSpinner } = useShowSpinner();

    const fee = registrationConfig.fees.find(
      (f) =>
        f.memTypeCode === membershipType.value &&
        f.memStatusCode ===
          (membershipStatusType.value === "NP1" ||
          membershipStatusType.value === "NP2"
            ? "ORD"
            : membershipStatusType.value),
    );

    let backTo = { name: "Diversity Details" };

    const pages = filterPages(ReviewDefinition.pages).filter((i) => {
      if (i.onlyOnActivating) return isActivating.value;

      return true;
    });

    const page = pages[0];

    async function moveNext() {
      const valid = await form.value.validate();
      if (!valid) return;
      setShowSpinner(true);
      const resp = await doPatch("/submit", {
        whereFound: whereFound.value,
        whereFoundOther: whereFoundOther.value,
        membershipNumber: existing.value ? membershipNumber.value : null,
        prePaid: !!isActivating.value,
      });

      if (resp.success) {
        if (resp.paymentUrl) {
          paymentUrl.value = resp.paymentUrl;
          root.$router.push({ path: "/register/membership/payment" });
        } else {
          root.$router.push({ path: "/register/thank-you" });
        }
      }
      setShowSpinner(false);

      showNotification(
        resp.success ? "success" : "error",
        resp.success
          ? "Successfully linked your membership"
          : resp.errors.includes("APT_ERR")
          ? 'An error has occurred creating your membership. <a href="/about/society-of-radiographers/contact-us">Please contact us for help</a>'
          : "Error linking your membership",
      );
    }

    return {
      backTo,
      moveNext,
      membershipNumber,
      existing,
      membershipType,
      membershipStatusType,
      fee,
      form,
      ...memberDetails,
      chosenWorkplace,
      isAgencyWorker,
      isActivating,
      country: computed(() => registrationConfig.countries[country.value]),
      gender: computed(() => registrationConfig.genders[gender.value]),
      nationality: computed(
        () => registrationConfig.nationalities[nationality.value],
      ),
      ethnicity: computed(
        () => registrationConfig.ethnicities[ethnicity.value],
      ),
      dateOfBirth: computed(() => {
        const d = new Date(dateOfBirth.value);
        const months = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];

        return `${d
          .getDate()
          .toString()
          .padStart(2, "0")} ${months[d.getMonth()]} ${d.getFullYear()}`;
      }),
      university: computed(
        () => registrationConfig.universities[university.value],
      ),
      universityOtherCountry: computed(
        () => registrationConfig.countries[universityOtherCountry.value],
      ),
      discipline: computed(
        () => registrationConfig.disciplines[discipline.value],
      ),
      qualificationOnEntry: computed(
        () =>
          registrationConfig.qualificationsOnEntry[qualificationOnEntry.value],
      ),
      course: computed(() => registrationConfig.courses[course.value]),
      fullPartTime: computed(
        () => registrationConfig.fullPartTime[fullPartTime.value],
      ),
      yearOfStudy: computed(
        () => registrationConfig.pointsOfEntry[memberDetails.yearOfStudy.value],
      ),
      membership: computed(() => {
        return registrationConfig.membershipStatuses[
          memberDetails.membershipStatusType.value
        ];
      }),
      showNewProfessionalBanner: computed(() =>
        ["NP1", "NP2"].includes(membershipStatusType.value),
      ),
      page,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/global-variables";

.review-section {
  h3 {
    font-size: 18px;
    font-weight: 700;
    line-height: 36px;
    margin-bottom: 8px;
  }

  p {
    color: var(--body-light);
    font-size: 14px;
    line-height: 28px;
    margin: 0;
  }

  .btn {
    min-width: 100px;

    @media all and (min-width: $screen-small) {
      float: right;
      margin-bottom: -32px;
      transform: translateY(calc(-100% - 4px));
    }
  }

  &:after {
    content: "";
    display: table;
    clear: both;
  }
}

.controls {
  align-items: center;
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
  padding: {
    top: 16px;
    bottom: 16px;
  }

  .btn {
    width: 180px;
    margin-left: 32px;
  }
}

.new-professionals-banner {
  padding: 1.5em;
  background: var(--primary);
  border-radius: 4px;
  margin-bottom: 1em;
  box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.13);
  color: #fff;

  > * {
    color: #fff;
  }
}
</style>
